import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import type { Listing, ListingType } from 'app/types/listing';
import type { RecentLoanListing } from 'app/network/loan';
import type { RecommendedListing } from 'app/types/recommender';
import type { SharingPlace } from 'app/types/sharing-place';

export enum Strategies {
  UsePredictions = 'predictions',
  UseUserItemPredictions = 'user-item',
  UsePastInteractions = 'past-interactions',
  UseTestRecommendations = 'test-recommendations',
  UseMixedRecommendations = 'mixed-recommendations',
  UseOneToManySimilarListings = 'one-to-many-similar-listings',
  UseMatchNeedsWithOffers = 'match-needs-with-offers',
  YourFavourites = 'your-favourites',
  UrgentNeeds = 'urgent-needs',
  RecentOffers = 'recent-offers',
  MostPopular = 'most-popular',
  RecentLoans = 'recent-loans',
  YourInterest = 'your-interest',
  ByCategory = 'by-category',
  NeighbourOffers = 'neighbour-offers',
  NeighbourRequests = 'neighbour-requests',
  NearestSharingPlace = 'nearest-sharing-place',
}

const API_STRATEGIES = new Set<Strategies>([
  Strategies.YourFavourites,
  Strategies.UrgentNeeds,
  Strategies.RecentOffers,
  Strategies.MostPopular,
  Strategies.RecentLoans,
  Strategies.YourInterest,
  Strategies.ByCategory,
  Strategies.NeighbourOffers,
  Strategies.NeighbourRequests,
  Strategies.NearestSharingPlace,
]);

export const isApiStrategy = (strategy: Strategies): boolean => API_STRATEGIES.has(strategy);

// These feeds use ordinary API queries rather than the recommendation service.
const isStandaloneDiscoveryStrategy = (strategy: Strategies): boolean =>
  [
    Strategies.UrgentNeeds,
    Strategies.RecentOffers,
    Strategies.RecentLoans,
    Strategies.YourFavourites,
    Strategies.MostPopular,
  ].includes(strategy);

export function isRecommendationFeatureEnabled(
  strategy: Strategies,
  featureFlags?: Parameters<typeof isFeatureEnabled>[1],
): boolean {
  return (
    [
      Strategies.NeighbourOffers,
      Strategies.NeighbourRequests,
      Strategies.NearestSharingPlace,
      Strategies.ByCategory,
    ].includes(strategy) ||
    (isFeatureEnabled(FeatureFlag.showDiscoveryFeeds, featureFlags) && isStandaloneDiscoveryStrategy(strategy)) ||
    isFeatureEnabled(FeatureFlag.showRecommendedForYou, featureFlags)
  );
}

export function shouldShowRecommendationSection(
  section: { featureEnabled: boolean; isPending: boolean; show: boolean },
  { hasEmptyState = false, ignoresFeatureFlag = false }: { hasEmptyState?: boolean; ignoresFeatureFlag?: boolean } = {},
) {
  return (ignoresFeatureFlag || section.featureEnabled) && (section.isPending || section.show || hasEmptyState);
}

export function getRecommendationTitleKey({
  strategy,
  recommendationType,
}: {
  strategy: Strategies;
  recommendationType?: ListingType;
}) {
  const titleKey = `${strategy}.title`;
  return strategy === Strategies.UseOneToManySimilarListings && recommendationType
    ? `${titleKey}.${recommendationType}`
    : titleKey;
}

export type RecommendationListItem = Listing | RecommendedListing | RecentLoanListing | SharingPlace;

export function getRecommendationItemBaseKey(item: RecommendationListItem, strategy: Strategies) {
  return `${strategy}-${item.id}`;
}

export function getRecommendationItemKeys(listings: RecommendationListItem[] | undefined, strategy: Strategies) {
  const seen = new Map<string, number>();
  return (
    listings?.map((item) => {
      const baseKey = getRecommendationItemBaseKey(item, strategy);
      const occurrence = seen.get(baseKey) ?? 0;
      seen.set(baseKey, occurrence + 1);
      return occurrence === 0 ? baseKey : `${baseKey}-${String(occurrence)}`;
    }) ?? []
  );
}
